import * as z from "zod";
import { RunProgressSchema } from "./domain.js";
import { Id, RunStatus } from "./ids.js";
import { ReviewStatus } from "./review.js";
import { TicketWorkSchema } from "./ticket-work.js";

export const TICKET_STATUSES = ["todo", "doing", "review", "blocked", "done", "closed"] as const;
export const TicketStatusSchema = z.enum(TICKET_STATUSES);
export type TicketStatus = z.infer<typeof TicketStatusSchema>;

/** `done` is finished-but-open for review; `closed` is terminal, so both stamp completion. */
export function isTicketCompletedStatus(status: TicketStatus): boolean {
  return status === "done" || status === "closed";
}

export const TICKET_PRIORITIES = ["low", "normal", "high", "urgent"] as const;
export const TicketPrioritySchema = z.enum(TICKET_PRIORITIES);
export type TicketPriority = z.infer<typeof TicketPrioritySchema>;
const DEFAULT_TICKET_PRIORITY: TicketPriority = "normal";

export const TICKET_TITLE_MAX_LENGTH = 200;
export const TICKET_DESCRIPTION_MAX_LENGTH = 20_000;
const TICKET_PREFIX_MAX_LENGTH = 12;
export const TICKET_COMMENT_MAX_LENGTH = 10_000;
/** Most words a ticket label may have. */
export const TICKET_LABEL_MAX_WORDS = 4;
/** Most characters a ticket label may have. */
export const TICKET_LABEL_MAX_LENGTH = 40;

/** jhl-Bots: a ticket's short title for to-do lists, 1 to 4 words, plain text. */
export const TicketLabelSchema = z
  .string()
  .trim()
  .min(1)
  .max(TICKET_LABEL_MAX_LENGTH)
  .refine((value) => value.split(/\s+/).length <= TICKET_LABEL_MAX_WORDS, {
    message: "Label must have at most 4 words",
  });

/**
 * jhl-Bots: the label to show, the set one or else the title's first words: at most four and
 * {@link TICKET_LABEL_MAX_LENGTH} characters, cut at a word, a single long word hard with "…".
 */
export function ticketLabel(label: string | null, title: string): string {
  if (label !== null) return label;
  const words = title.trim().split(/\s+/).slice(0, TICKET_LABEL_MAX_WORDS);
  while (words.length > 1 && words.join(" ").length > TICKET_LABEL_MAX_LENGTH) words.pop();
  const short = words.join(" ");
  return short.length > TICKET_LABEL_MAX_LENGTH
    ? `${short.slice(0, TICKET_LABEL_MAX_LENGTH - 1)}…`
    : short;
}

const TicketPrefixSchema = z
  .string()
  .trim()
  .min(1)
  .max(TICKET_PREFIX_MAX_LENGTH)
  .regex(
    /^[A-Za-z][A-Za-z0-9]*$/,
    "Prefix must start with a letter and contain only letters and digits",
  );

export function ticketRef(prefix: string, number: number): string {
  return `${prefix}-${number}`;
}

export function parseTicketRef(
  value: string,
  expectedPrefix?: string,
): { prefix: string; number: number } | null {
  const match = /^([A-Za-z][A-Za-z0-9]*)-([1-9][0-9]*)$/.exec(value.trim());
  if (!match) return null;
  const [, rawPrefix, rawNumber] = match;
  if (!rawPrefix || !rawNumber) return null;
  const prefix = rawPrefix.toUpperCase();
  if (expectedPrefix !== undefined && prefix !== expectedPrefix.trim().toUpperCase()) return null;
  return { prefix, number: Number(rawNumber) };
}

export const BoardSchema = z.object({
  id: Id,
  spaceId: Id,
  name: z.string(),
  ticketPrefix: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Board = z.infer<typeof BoardSchema>;

/** Renaming a board or changing its ticket prefix; at least one of the two. */
export const RenameBoardInput = z
  .object({
    name: z.string().trim().min(1).max(80).optional(),
    ticketPrefix: TicketPrefixSchema.optional(),
  })
  .refine((value) => value.name !== undefined || value.ticketPrefix !== undefined, {
    message: "Provide a name or ticketPrefix",
  });
/** Validated board rename. */
export type RenameBoardInput = z.infer<typeof RenameBoardInput>;

export const TicketCommentSchema = z.object({
  id: Id,
  ticketId: Id,
  body: z.string(),
  authorBotId: Id.nullable(),
  authorUserId: Id.nullable(),
  authorName: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type TicketComment = z.infer<typeof TicketCommentSchema>;

/** A ticket as the API returns it; its label is always set, from the title when none was given. */
export const TicketSchema = z.object({
  id: Id,
  boardId: Id,
  spaceId: Id,
  number: z.number().int().positive(),
  ref: z.string(),
  title: z.string(),
  /** jhl-Bots: short title, the set label or else from the title, see {@link ticketLabel}. */
  label: z.string(),
  description: z.string().nullable(),
  status: TicketStatusSchema,
  priority: TicketPrioritySchema,
  assigneeBotId: Id.nullable(),
  createdByBotId: Id.nullable(),
  createdByUserId: Id.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  completedAt: z.string().nullable(),
  /** jhl-Bots: the work state that keeps the ticket going (ticket-work.ts); absent before it has one. */
  work: TicketWorkSchema.nullable().optional(),
});
export type Ticket = z.infer<typeof TicketSchema>;

/** Filters for listing a board's tickets. */
export const ListTicketsInput = z.object({
  boardId: Id.optional(),
  status: TicketStatusSchema.optional(),
  assigneeBotId: Id.optional(),
  q: z.string().trim().max(200).optional(),
});
/** Validated ticket list filters. */
export type ListTicketsInput = z.infer<typeof ListTicketsInput>;

/** A board's tickets. */
export const ListTicketsOutput = z.object({ tickets: z.array(TicketSchema) });
/** A board's tickets, typed. */
export type ListTicketsOutput = z.infer<typeof ListTicketsOutput>;

/** One ticket by id or by reference such as RAK-7. */
export const GetTicketInput = z
  .object({
    id: Id.optional(),
    ref: z.string().trim().min(1).max(120).optional(),
  })
  .refine((value) => Boolean(value.id) !== Boolean(value.ref), {
    message: "Provide exactly one of id or ref",
  });
/** Validated ticket lookup. */
export type GetTicketInput = z.infer<typeof GetTicketInput>;

/** A new ticket from the board, with its owner bot. */
export const CreateTicketInput = z.object({
  boardId: Id.optional(),
  title: z.string().trim().min(1).max(TICKET_TITLE_MAX_LENGTH),
  label: TicketLabelSchema.optional(),
  description: z.string().max(TICKET_DESCRIPTION_MAX_LENGTH).optional(),
  priority: TicketPrioritySchema.default(DEFAULT_TICKET_PRIORITY),
  assigneeBotId: Id,
});
/** Validated new ticket. */
export type CreateTicketInput = z.infer<typeof CreateTicketInput>;

/** Changes to a ticket; fields left out stay as they are. */
export const UpdateTicketInput = z.object({
  id: Id,
  title: z.string().trim().min(1).max(TICKET_TITLE_MAX_LENGTH).optional(),
  /** null removes the label, so the title's first words show again. */
  label: TicketLabelSchema.nullable().optional(),
  description: z.string().max(TICKET_DESCRIPTION_MAX_LENGTH).nullable().optional(),
  priority: TicketPrioritySchema.nullable().optional(),
  status: TicketStatusSchema.optional(),
  assigneeBotId: Id.optional(),
});
/** Validated ticket changes. */
export type UpdateTicketInput = z.infer<typeof UpdateTicketInput>;

/** Moving a ticket to another status column. */
export const MoveTicketInput = z.object({
  id: Id,
  status: TicketStatusSchema,
});
/** Validated status move. */
export type MoveTicketInput = z.infer<typeof MoveTicketInput>;

/** A comment on a ticket. */
export const CommentTicketInput = z.object({
  ticketId: Id,
  body: z.string().trim().min(1).max(TICKET_COMMENT_MAX_LENGTH),
});
/** Validated ticket comment. */
export type CommentTicketInput = z.infer<typeof CommentTicketInput>;

/**
 * One frame of boards.subscribe, always for the subscriber's own space. `kind` says what it is:
 * `heartbeat` only proves the stream lives, `ticket` is a board or ticket change, and `run` is a
 * status change of a run of one of the subscriber's own bots, whatever started it (chat, ticket
 * session, routine, agent connection), or a changed plan progress of its running run. Only that
 * frame carries `progress` ({done, total}, status running); it replaces the bot's progress until
 * the next one, and a status other than running clears it. Ticket frames reach the whole space,
 * run frames only the run's user. A run frame carries no thread, message or preview text. A
 * `review` frame says a review card of the subscriber changed and reaches only its recipient. Its
 * status is a hint to reload, not state: PostgreSQL folds identical signals of one transaction
 * into one, so a client must not count frames or replay them as a history.
 */
export const BoardEventSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("heartbeat"), spaceId: Id, createdAt: z.string() }),
  z.object({
    kind: z.literal("ticket"),
    spaceId: Id,
    boardId: Id.optional(),
    ticketId: Id.optional(),
    createdAt: z.string(),
  }),
  z.object({
    kind: z.literal("run"),
    spaceId: Id,
    botId: Id,
    status: RunStatus,
    progress: RunProgressSchema.optional(),
    createdAt: z.string(),
  }),
  /** jhl-Bots (RAK-425): a review card of the subscriber changed; reload reviews.count (and list). */
  z.object({
    kind: z.literal("review"),
    spaceId: Id,
    reviewId: Id,
    status: ReviewStatus,
    createdAt: z.string(),
  }),
]);
/** One validated frame of boards.subscribe. */
export type BoardEvent = z.infer<typeof BoardEventSchema>;
